import type { TraceEvent } from '@grafana/faro-web-sdk';
import type { ClockSample } from './clockOffset.ts';

export const CLOCK_OFFSET_ATTRIBUTE = 'browser.clock_offset_ms';

type ResourceSpans = NonNullable<TraceEvent['resourceSpans']>[number];
type ScopeSpans = ResourceSpans['scopeSpans'][number];
type Span = NonNullable<ScopeSpans['spans']>[number];
type SpanEvent = NonNullable<Span['events']>[number];
type UnixNano = Span['startTimeUnixNano'];

const NANOS_PER_MILLI = 1_000_000;

export type Timestamped = { timestamp: string };

export function shiftTimestampOntoServerClock<T extends Timestamped>(payload: T, sample: ClockSample): T {
  return { ...payload, timestamp: new Date(Date.parse(payload.timestamp) + sample.offsetMs).toISOString() };
}

function shiftNanos(timestamp: UnixNano, offsetNs: bigint): UnixNano {
  if (typeof timestamp === 'string') return (BigInt(timestamp) + offsetNs).toString();
  if (typeof timestamp === 'number') return Number(BigInt(Math.trunc(timestamp)) + offsetNs);
  return timestamp;
}

function shiftEvent(event: SpanEvent, offsetNs: bigint): SpanEvent {
  return { ...event, timeUnixNano: shiftNanos(event.timeUnixNano, offsetNs) };
}

function shiftSpan(span: Span, sample: ClockSample, offsetNs: bigint): Span {
  return {
    ...span,
    attributes: [...span.attributes, { key: CLOCK_OFFSET_ATTRIBUTE, value: { doubleValue: sample.offsetMs } }],
    endTimeUnixNano: shiftNanos(span.endTimeUnixNano, offsetNs),
    events: span.events.map((event) => shiftEvent(event, offsetNs)),
    startTimeUnixNano: shiftNanos(span.startTimeUnixNano, offsetNs),
  };
}

export function shiftTraceOntoServerClock(trace: TraceEvent, sample: ClockSample): TraceEvent {
  const offsetNs = BigInt(Math.round(sample.offsetMs * NANOS_PER_MILLI));

  return {
    ...trace,
    resourceSpans: trace.resourceSpans?.map((resourceSpans) => ({
      ...resourceSpans,
      scopeSpans: resourceSpans.scopeSpans.map((scopeSpans) => ({
        ...scopeSpans,
        spans: scopeSpans.spans?.map((span) => shiftSpan(span, sample, offsetNs)),
      })),
    })),
  };
}
